import React, { useState } from 'react';
import { OfficeMap } from '../components/Map';
import { useTranslations } from '../context/TranslationContext';
import useAnalytics from '../hooks/useAnalytics';

export const ContactSection: React.FC = () => {
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const placeId = "0x474869fbb041eaa7:0x5626ed7e10ec51b8";
  const googleMapsUrl = `https://www.google.com/maps/place/Cabinet+de+Avocat+Kovács+Timea/@47.1792004,23.0540109,19z/data=!3m1!4b1!4m6!3m5!1s${placeId}!8m2!3d47.1792004!4d23.0540109!16s%2Fg%2F11t00gsjw8`;

  const trackFormSubmission = (status: 'success' | 'error') => {
    trackEvent('form_submission', {
      form_name: 'contact',
      submission_status: status,
      timestamp: new Date().toISOString()
    });
  };

  const trackFieldInteraction = (fieldName: string) => {
    trackEvent('form_field_interaction', {
      form_name: 'contact',
      field_name: fieldName,
      interaction_type: 'focus'
    });
  };

  const trackFormStart = () => {
    trackEvent('form_start', {
      form_name: 'contact',
      timestamp: new Date().toISOString()
    });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const formEntries = {
        'entry.918610683': formData.get('name')?.toString() ?? '',
        'entry.202993236': formData.get('email')?.toString() ?? '',
        'entry.762484117': formData.get('phone')?.toString() ?? '',
        'entry.1596383647': formData.get('message')?.toString() ?? ''
      } as Record<string, string>;
      const formId = process.env.REACT_APP_GOOGLE_FORM_ID;
      await fetch(`https://docs.google.com/forms/d/e/${formId}/formResponse`, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formEntries)
      });
      setSubmitStatus('success');
      trackFormSubmission('success');
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      trackFormSubmission('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <section id="contact" className="py-32 bg-white border-t border-hair">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-reveal>
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">05 / {t.contact.title}</div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="display text-[42px] md:text-[56px] mb-6">{t.contact.title}</h2>
            <p className="display text-[26px] leading-[1.45] text-ink mb-4 max-w-2xl">
              {t.contact.lead}
            </p>
            <p className="text-mute text-base leading-[1.8] max-w-2xl font-light">
              {t.contact.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <form className="lg:col-span-7 space-y-8" data-reveal onSubmit={onSubmit}>
            <div>
              <label className="field-label mb-2 block">{t.contact.form.name.label}</label>
              <input
                type="text"
                name="name"
                className="field-line"
                required
                onFocus={() => trackFieldInteraction('name')}
                onChange={(e) => {
                  if (e.target.value.length === 1) trackFormStart();
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="field-label mb-2 block">{t.contact.form.email.label}</label>
                <input
                  type="email"
                  name="email"
                  className="field-line"
                  required
                  onFocus={() => trackFieldInteraction('email')}
                />
              </div>
              <div>
                <label className="field-label mb-2 block">{t.contact.form.phone.label}</label>
                <input
                  type="tel"
                  name="phone"
                  className="field-line"
                  onFocus={() => trackFieldInteraction('phone')}
                />
              </div>
            </div>
            <div>
              <label className="field-label mb-2 block">{t.contact.form.message.label}</label>
              <textarea
                name="message"
                className="field-line"
                rows={4}
                required
                onFocus={() => trackFieldInteraction('message')}
              />
            </div>
            <div className="pt-4">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                <span>{isSubmitting ? t.contact.form.submitButton.sending : t.contact.form.submitButton.default}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-accent/10 text-accent text-sm">{t.contact.form.success}</div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 text-red-700 text-sm">{t.contact.form.error}</div>
            )}
          </form>

          <div className="lg:col-span-5 space-y-10" data-reveal data-reveal-delay="200">
            <div>
              <div className="eyebrow mb-3">{t.contact.info.title}</div>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="link-grow text-[15px] font-light"
                onClick={() => trackEvent('contact_action', { action_type: 'map_click', destination: 'google_maps' })}
              >
                {t.contact.info.address}
              </a>
            </div>
            <div>
              <div className="eyebrow mb-3">Email</div>
              <a
                href="mailto:avocat.kt@gmail.com"
                className="link-grow text-[15px] font-light"
                onClick={() => trackEvent('contact_action', { action_type: 'email_click', destination: 'avocat.kt@gmail.com' })}
              >
                avocat.kt@gmail.com
              </a>
            </div>
            <div>
              <div className="eyebrow mb-3">Telefon</div>
              <a
                href="tel:+40740245886"
                className="link-grow text-[15px] font-light"
                onClick={() => trackEvent('contact_action', { action_type: 'phone_click', destination: '+40740245886' })}
              >
                +40 740 245 886
              </a>
            </div>
            <div>
              <div className="eyebrow mb-3">{t.contact.info.reviews.label}</div>
              <a
                href={t.contact.info.reviews.url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-grow text-[15px] font-light"
                onClick={() => trackEvent('contact_action', { action_type: 'reviews_click', destination: t.contact.info.reviews.url })}
              >
                {t.contact.info.reviews.value}
              </a>
              <div className="text-[11px] text-mute mt-2">{t.contact.info.reviews.asOf}</div>
            </div>
            <div className="mt-6">
              <OfficeMap googleMapsUrl={googleMapsUrl} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
